import React, { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../LanguageProvider';
import { 
  processDocument, 
  analyzePropertyValue, 
  getCompletePropertyAnalysis,
  generateAllVisualizations 
} from '../services/aiServices';
import { checkFreeListingQuota, useFreeListingQuota } from '../services/promoServices';
import { auth } from '../firebase-config';
import BoostUpsellModal from '../components/hopphomes/BoostUpsellModal';
import VideoUpsellModal from '../components/hopphomes/VideoUpsellModal';
import PremiumUpsellModal from '../components/hopphomes/PremiumUpsellModal';
import BundleOfferModal from '../components/hopphomes/BundleOfferModal';
import StagingUpsellModal from '../components/hopphomes/StagingUpsellModal';
import AIScoreCard from '../components/hopphomes/AIScoreCard';
import AIProSubscriptionModal from '../components/hopphomes/AIProSubscriptionModal';

// Lazy load visualization components for better performance
const ModelViewer3D = lazy(() => import('../components/visualization/ModelViewer3D'));
const FloorPlanGenerator = lazy(() => import('../components/visualization/FloorPlanGenerator'));
const InteriorRenderer = lazy(() => import('../components/visualization/InteriorRenderer'));
const VirtualTourGenerator = lazy(() => import('../components/visualization/VirtualTourGenerator'));
const ARVRSupport = lazy(() => import('../components/visualization/ARVRSupport'));
const PremiumPackages = lazy(() => import('../components/visualization/PremiumPackages'));

export default function PropertyUpload() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [showStagingModal, setShowStagingModal] = useState(false);
  const [showAIProModal, setShowAIProModal] = useState(false);
  const [listingId, setListingId] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  const [aiIssues, setAiIssues] = useState([]);

  // Check free listing quota on component mount
  React.useEffect(() => {
    const checkQuota = async () => {
      const user = auth.currentUser;
      if (user) {
        const quotaResult = await checkFreeListingQuota(user.uid);
        if (quotaResult.success) {
          setFreeListingAvailable(quotaResult.hasFreeListing);
          setRemainingFreeListings(quotaResult.remainingListings || 0);
        }
      }
    };
    checkQuota();
  }, []);
  const [propertyData, setPropertyData] = useState({
    type: '',
    title: '',
    description: '',
    price: '',
    location: '',
    size: '',
    rooms: '',
    images: [],
    documents: [],
    visualizationPackage: 'basic',
    floorPlan: null,
    renderedImages: [],
    virtualTour: null,
    modelUrl: null
  });
  const [activeVisualization, setActiveVisualization] = useState('overview');
  const [analysisResults, setAnalysisResults] = useState(null);
  const [documentResults, setDocumentResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [visualizationResults, setVisualizationResults] = useState(null);
  const [freeListingAvailable, setFreeListingAvailable] = useState(false);
  const [remainingFreeListings, setRemainingFreeListings] = useState(0);

  const steps = [
    { id: 1, title: 'Ingatlan típusa', icon: '🏠' },
    { id: 2, title: 'Alapadatok', icon: '📋' },
    { id: 3, title: 'Képek és dokumentumok', icon: '📸' },
    { id: 4, title: 'AI elemzés', icon: '🤖' },
    { id: 5, title: '3D Vizualizáció', icon: '🎮' },
    { id: 6, title: 'Csomag választás', icon: '💎' },
    { id: 7, title: 'Véglegesítés', icon: '✅' }
  ];

  const propertyTypes = [
    { id: 'house', name: 'Családi ház', icon: '🏡', description: 'Önálló családi ház telekkel' },
    { id: 'apartment', name: 'Lakás', icon: '🏢', description: 'Társasházi vagy panellakás' },
    { id: 'plot', name: 'Telek', icon: '🌳', description: 'Építési vagy mezőgazdasági telek' },
    { id: 'mobile', name: 'Mobilház', icon: '🏠', description: 'Mobilház telekkel vagy anélkül' },
    { id: 'commercial', name: 'Kereskedelmi', icon: '🏪', description: 'Üzlet, iroda vagy raktár' },
    { id: 'investment', name: 'Befektetési', icon: '💰', description: 'Befektetési célú ingatlan' }
  ];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePublishListing = async () => {
    // Save listing to Firebase
    const newListingId = `listing_${Date.now()}`;
    setListingId(newListingId);
    
    // Calculate AI Score
    const score = calculateAIScore();
    setAiScore(score);
    
    // Show Bundle offer first (10 min timer)
    setShowBundleModal(true);
  };

  const calculateAIScore = () => {
    let score = 10;
    const issues = [];

    // Check for issues
    if (!propertyData.images || propertyData.images.length < 3) {
      score -= 2;
      issues.push('Kevés kép (minimum 5 ajánlott)');
    }
    if (!propertyData.description || propertyData.description.length < 100) {
      score -= 1.5;
      issues.push('Rövid leírás');
    }
    // Add more checks as needed
    
    setAiIssues(issues);
    return Math.max(0, score);
  };

  const handleBundleSelect = (bundle) => {
    console.log('Bundle selected:', bundle);
    setShowBundleModal(false);
    // Navigate to success
    navigate('/property-success', { state: { listingData: propertyData, listingId } });
  };

  const handleBoostSelect = (boostPlan) => {
    console.log('Boost selected:', boostPlan);
    setShowBoostModal(false);
    // Show second upsell: Video
    setShowVideoModal(true);
  };

  const handleVideoSelect = (videoPlan) => {
    console.log('Video selected:', videoPlan);
    setShowVideoModal(false);
    // Show staging upsell
    setShowStagingModal(true);
  };

  const handleStagingSelect = (stagingPlan) => {
    console.log('Staging selected:', stagingPlan);
    setShowStagingModal(false);
    // Show premium upsell
    setShowPremiumModal(true);
  };

  const handlePremiumSelect = (premiumData) => {
    console.log('Premium selected:', premiumData);
    setShowPremiumModal(false);
    // Navigate to success page
    navigate('/property-success', { state: { listingData: propertyData, listingId } });
  };

  const handleSkipUpsells = () => {
    setShowBoostModal(false);
    setShowVideoModal(false);
    setShowPremiumModal(false);
    setShowBundleModal(false);
    setShowStagingModal(false);
    setShowAIProModal(false);
    // Navigate to success page
    navigate('/property-success', { state: { listingData: propertyData, listingId } });
  };

  const handleImproveScore = () => {
    // Show boost modal to improve score
    setShowBoostModal(true);
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleTypeSelect = (type) => {
    setPropertyData({ ...propertyData, type });
    handleNext();
  };

  // Process documents with real OCR
  const handleDocumentUpload = async (files) => {
    setIsProcessing(true);
    const propertyId = `property_${Date.now()}`;
    const documents = [];
    
    for (const file of files) {
      // In real implementation, upload file to Firebase Storage first
      const fileUrl = URL.createObjectURL(file); // Mock URL for demo
      documents.push({
        fileUrl,
        fileName: file.name,
        propertyId
      });
    }
    
    try {
      const results = await getCompletePropertyAnalysis(propertyId, propertyData, documents);
      if (results.success) {
        setDocumentResults(results.documents);
        setAnalysisResults(results.analysis);
      }
    } catch (error) {
      console.error('Document processing failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Generate AI visualizations
  const handleVisualizationGeneration = async (visualizationType) => {
    setIsProcessing(true);
    const propertyId = `property_${Date.now()}`;
    
    try {
      const options = {
        include3D: visualizationType === '3dmodel' || visualizationType === 'all',
        includeFloorPlan: visualizationType === 'floorplan' || visualizationType === 'all',
        includeInterior: visualizationType === 'interior' || visualizationType === 'all',
        includeVirtualTour: visualizationType === 'virtualtour' || visualizationType === 'all',
        includeARVR: visualizationType === 'arvr' || visualizationType === 'all'
      };
      
      const results = await generateAllVisualizations(propertyId, options);
      if (results.success) {
        setVisualizationResults(results.visualizations);
      }
    } catch (error) {
      console.error('Visualization generation failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Early Registration Promotion Banner */}
            {freeListingAvailable && remainingFreeListings > 0 && (
              <div className="bg-gradient-to-r from-[#e31937] to-[#b40000] rounded-xl p-6 mb-8 shadow-xl border border-red-400/30">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="text-2xl">🎁</span>
                    <h3 className="text-xl md:text-2xl font-bold text-white">
                      Ingyenes feltöltés elérhető!
                    </h3>
                  </div>
                  <p className="text-white/90 text-base mb-3">
                    Még <span className="font-bold text-yellow-200">{remainingFreeListings} ingyenes hirdetésed</span> van hátra
                  </p>
                  <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <p className="text-white text-sm font-medium">
                      🐦 Early Bird előny – 2025. szeptember 30-ig érvényes
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Milyen típusú ingatlant szeretne feltölteni?</h2>
              <p className="text-gray-600">Válassza ki az ingatlan típusát a megfelelő adatbekéréshez</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {propertyTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleTypeSelect(type.id)}
                  className="p-6 bg-white/90 backdrop-blur-sm border-2 border-white/50 rounded-xl hover:border-[#e31937] hover:bg-white hover:shadow-2xl hover:shadow-[#e31937]/20 transition-all duration-300 text-left group transform hover:scale-105"
                >
                  <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">{type.icon}</div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-[#e31937] transition-colors duration-300">{type.name}</h3>
                  <p className="text-gray-700 text-sm group-hover:text-gray-900 transition-colors duration-300">{type.description}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Alapadatok megadása</h2>
              <p className="text-gray-600">Adja meg az ingatlan alapvető információit</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ingatlan címe *</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e31937] focus:border-transparent"
                  placeholder="Pl. Budapest, Váci út 1."
                  value={propertyData.title}
                  onChange={(e) => setPropertyData({ ...propertyData, title: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ár (Ft) *</label>
                <input
                  type="number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e31937] focus:border-transparent"
                  placeholder="Pl. 45000000"
                  value={propertyData.price}
                  onChange={(e) => setPropertyData({ ...propertyData, price: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alapterület (m²) *</label>
                <input
                  type="number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e31937] focus:border-transparent"
                  placeholder="Pl. 120"
                  value={propertyData.size}
                  onChange={(e) => setPropertyData({ ...propertyData, size: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Szobák száma</label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e31937] focus:border-transparent"
                  value={propertyData.rooms}
                  onChange={(e) => setPropertyData({ ...propertyData, rooms: e.target.value })}
                >
                  <option value="">Válasszon</option>
                  <option value="1">1 szoba</option>
                  <option value="2">2 szoba</option>
                  <option value="3">3 szoba</option>
                  <option value="4">4 szoba</option>
                  <option value="5+">5+ szoba</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Leírás</label>
                <textarea
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e31937] focus:border-transparent"
                  placeholder="Írja le az ingatlan jellemzőit, előnyeit..."
                  value={propertyData.description}
                  onChange={(e) => setPropertyData({ ...propertyData, description: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Képek és dokumentumok</h2>
              <p className="text-gray-600">Töltse fel az ingatlan képeit és dokumentumait</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Image Upload */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📸 Képek feltöltése</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#e31937] transition-colors">
                  <div className="text-4xl mb-4">🖼️</div>
                  <p className="text-gray-600 mb-4">Húzza ide a képeket vagy kattintson a tallózáshoz</p>
                  <button className="bg-[#e31937] text-white px-6 py-2 rounded-lg hover:bg-[#b91c1c] transition-colors">
                    Képek kiválasztása
                  </button>
                  <p className="text-xs text-gray-500 mt-2">Támogatott formátumok: JPG, PNG, WebP (max. 10MB/kép)</p>
                </div>
              </div>

              {/* Document Upload */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📄 Dokumentumok feltöltése</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#e31937] transition-colors">
                  <div className="text-4xl mb-4">📋</div>
                  <p className="text-gray-600 mb-4">Tulajdoni lap, alaprajz, energetikai tanúsítvány</p>
                  <button className="bg-[#e31937] text-white px-6 py-2 rounded-lg hover:bg-[#b91c1c] transition-colors">
                    Dokumentumok kiválasztása
                  </button>
                  <p className="text-xs text-gray-500 mt-2">Támogatott formátumok: PDF, DOC, JPG (max. 20MB/fájl)</p>
                </div>
              </div>
            </div>

            {/* AI Features Preview */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🤖 AI Funkciók</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-800">
                <div className="flex items-center space-x-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-800">Automatikus képoptimalizálás</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-800">Dokumentum OCR felismerés</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-800">Fraud detection ellenőrzés</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Elemzés és Validáció</h2>
              <p className="text-gray-600">Mesterséges intelligencia ellenőrzi az ingatlan adatait</p>
            </div>
            
            <div className="space-y-4">
              {/* AI Analysis Steps */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">🔍 Képek elemzése</h3>
                  <span className="text-green-500 font-medium">✓ Kész</span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• Képminőség ellenőrzése: Kiváló</p>
                  <p>• Fraud detection: Nem észlelt gyanús tartalmat</p>
                  <p>• Automatikus kategorizálás: Családi ház</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">📋 Dokumentum feldolgozás</h3>
                  <span className="text-yellow-500 font-medium">⏳ Folyamatban...</span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• OCR szövegfelismerés: 95% pontosság</p>
                  <p>• Tulajdoni lap validáció: Ellenőrzés alatt</p>
                  <p>• Energetikai besorolás: B kategória</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">💰 Piaci elemzés</h3>
                  <span className="text-blue-500 font-medium">🔄 Várakozik</span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• Környékbeli árak elemzése</p>
                  <p>• Befektetési potenciál számítása</p>
                  <p>• Ajánlott ár meghatározása</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 AI Ajánlások</h3>
              <div className="space-y-2 text-sm text-gray-800">
                <p className="text-gray-800"><strong className="text-gray-900">Ajánlott ár:</strong> 42,500,000 - 47,500,000 Ft</p>
                <p className="text-gray-800"><strong className="text-gray-900">Várható értékesítési idő:</strong> 2-4 hónap</p>
                <p className="text-gray-800"><strong className="text-gray-900">Befektetési potenciál:</strong> Magas (8.5/10)</p>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">3D Vizualizáció és AI Renderelés</h2>
              <p className="text-gray-600">Prémium vizualizációs funkciók az ingatlan bemutatásához</p>
            </div>
            
            {/* Visualization Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              <button
                onClick={() => setActiveVisualization('overview')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeVisualization === 'overview'
                    ? 'bg-[#e31937] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                📊 Áttekintés
              </button>
              <button
                onClick={() => setActiveVisualization('3dmodel')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeVisualization === '3dmodel'
                    ? 'bg-[#e31937] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🎮 3D Modell
              </button>
              <button
                onClick={() => setActiveVisualization('floorplan')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeVisualization === 'floorplan'
                    ? 'bg-[#e31937] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                📐 Alaprajz
              </button>
              <button
                onClick={() => setActiveVisualization('interior')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeVisualization === 'interior'
                    ? 'bg-[#e31937] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🎨 Belső tér
              </button>
              <button
                onClick={() => setActiveVisualization('tour')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeVisualization === 'tour'
                    ? 'bg-[#e31937] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🎬 Virtuális túra
              </button>
              <button
                onClick={() => setActiveVisualization('arvr')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeVisualization === 'arvr'
                    ? 'bg-[#e31937] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🥽 AR/VR
              </button>
            </div>
            
            {/* Visualization Content */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <Suspense fallback={
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e31937]"></div>
                </div>
              }>
                {activeVisualization === 'overview' && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-gray-900">🚀 HoppAI Pro Vizualizációs Csomag</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                        <div className="text-3xl mb-2">🎮</div>
                        <h4 className="font-semibold text-blue-900">3D Modell</h4>
                        <p className="text-sm text-blue-700 mt-1">Interaktív 3D megjelenítés</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                        <div className="text-3xl mb-2">📐</div>
                        <h4 className="font-semibold text-green-900">AI Alaprajz</h4>
                        <p className="text-sm text-green-700 mt-1">Automatikus alaprajz generálás</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                        <div className="text-3xl mb-2">🎨</div>
                        <h4 className="font-semibold text-purple-900">Belső Renderelés</h4>
                        <p className="text-sm text-purple-700 mt-1">AI-alapú látványtervek</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                        <div className="text-3xl mb-2">🎬</div>
                        <h4 className="font-semibold text-orange-900">Virtuális Túra</h4>
                        <p className="text-sm text-orange-700 mt-1">360° interaktív bemutató</p>
                      </div>
                      <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                        <div className="text-3xl mb-2">🥽</div>
                        <h4 className="font-semibold text-red-900">AR/VR Támogatás</h4>
                        <p className="text-sm text-red-700 mt-1">Kiterjesztett valóság</p>
                      </div>
                      <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                        <div className="text-3xl mb-2">💎</div>
                        <h4 className="font-semibold text-yellow-900">Prémium Csomag</h4>
                        <p className="text-sm text-yellow-700 mt-1">Minden funkció elérhető</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                      <h4 className="font-semibold text-gray-900 mb-3">✨ Miért válassza a HoppAI Pro-t?</h4>
                      <ul className="space-y-2 text-sm text-gray-800">
                        <li className="flex items-center space-x-2">
                          <span className="text-green-500">✓</span>
                          <span className="text-gray-800">GPT-5 és Stable Diffusion AI technológia</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-500">✓</span>
                          <span className="text-gray-800">Fotórealisztikus renderelés percek alatt</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-500">✓</span>
                          <span className="text-gray-800">Mobil AR és VR headset támogatás</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-500">✓</span>
                          <span className="text-gray-800">CDN alapú gyors betöltés világszerte</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {activeVisualization === '3dmodel' && (
                  <ModelViewer3D modelUrl={propertyData.modelUrl} />
                )}
                {activeVisualization === 'floorplan' && (
                  <FloorPlanGenerator 
                    propertyData={propertyData}
                    onGenerate={(floorPlan) => setPropertyData({...propertyData, floorPlan})}
                  />
                )}
                {activeVisualization === 'interior' && (
                  <InteriorRenderer 
                    onRender={(image) => setPropertyData({...propertyData, renderedImages: [...propertyData.renderedImages, image]})}
                  />
                )}
                {activeVisualization === 'tour' && (
                  <VirtualTourGenerator 
                    propertyData={propertyData}
                    images={propertyData.images}
                  />
                )}
                {activeVisualization === 'arvr' && (
                  <ARVRSupport 
                    propertyData={propertyData}
                    modelUrl={propertyData.modelUrl}
                  />
                )}
              </Suspense>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <Suspense fallback={
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e31937]"></div>
              </div>
            }>
              <PremiumPackages 
                selectedPackage={propertyData.visualizationPackage}
                onSelectPackage={(pkg) => setPropertyData({...propertyData, visualizationPackage: pkg})}
              />
            </Suspense>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Hirdetés véglegesítése</h2>
              <p className="text-gray-600">Ellenőrizze az adatokat és tegye közzé a hirdetést</p>
            </div>

            {/* Summary */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">📋 Összefoglaló</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Típus:</strong> {propertyTypes.find(t => t.id === propertyData.type)?.name}</p>
                  <p><strong>Cím:</strong> {propertyData.title}</p>
                  <p><strong>Ár:</strong> {propertyData.price ? `${parseInt(propertyData.price).toLocaleString()} Ft` : '-'}</p>
                </div>
                <div>
                  <p><strong>Alapterület:</strong> {propertyData.size} m²</p>
                  <p><strong>Szobák:</strong> {propertyData.rooms || '-'}</p>
                  <p><strong>Képek:</strong> {propertyData.images.length} db</p>
                </div>
              </div>
            </div>

            {/* Monetization Options */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">💰 Jutalékos opciók</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="w-4 h-4 text-[#e31937]" />
                  <span className="text-sm">Részvétel az affiliate programban (3% jutalék sikeres értékesítés esetén)</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="w-4 h-4 text-[#e31937]" />
                  <span className="text-sm">HOPPx token cashback engedélyezése vevőknek (1% visszatérítés)</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="w-4 h-4 text-[#e31937]" />
                  <span className="text-sm">Co-buying lehetőség engedélyezése (több vevő közös vásárlása)</span>
                </label>
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center space-x-3 mb-3">
                <span className="text-2xl">🎉</span>
                <h3 className="text-lg font-semibold text-green-800">Hirdetés készen áll a közzétételre!</h3>
              </div>
              <p className="text-sm text-green-700 mb-4">Az AI elemzés sikeresen lezárult, a hirdetés megfelel a minőségi követelményeknek.</p>
              <button
                onClick={handlePublishListing}
                className="w-full bg-gradient-to-r from-[#e31937] to-red-600 hover:from-red-600 hover:to-[#e31937] text-white font-black py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all"
              >
                🚀 Hirdetés közzététele
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative py-8 overflow-hidden">
      {/* Animated AI Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-slate-800">
          {/* Floating Houses */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 text-4xl opacity-20 animate-bounce" style={{animationDelay: '0s'}}>🏠</div>
            <div className="absolute top-40 right-20 text-3xl opacity-15 animate-bounce" style={{animationDelay: '1s'}}>🏡</div>
            <div className="absolute bottom-32 left-1/4 text-5xl opacity-10 animate-bounce" style={{animationDelay: '2s'}}>🏘️</div>
            <div className="absolute top-1/3 right-1/3 text-2xl opacity-25 animate-bounce" style={{animationDelay: '1.5s'}}>🏢</div>
            <div className="absolute bottom-20 right-10 text-3xl opacity-20 animate-bounce" style={{animationDelay: '3s'}}>🏠</div>
            <div className="absolute top-60 left-1/3 text-4xl opacity-15 animate-bounce" style={{animationDelay: '2.5s'}}>🏡</div>
          </div>
          
          {/* Money & AI Symbols */}
          <div className="absolute inset-0">
            <div className="absolute top-32 right-1/4 text-2xl opacity-20 animate-pulse" style={{animationDelay: '0.5s'}}>💰</div>
            <div className="absolute bottom-40 left-20 text-xl opacity-15 animate-pulse" style={{animationDelay: '1.2s'}}>💎</div>
            <div className="absolute top-1/2 left-10 text-2xl opacity-25 animate-pulse" style={{animationDelay: '2.1s'}}>🤖</div>
            <div className="absolute bottom-60 right-1/3 text-xl opacity-20 animate-pulse" style={{animationDelay: '1.8s'}}>⚡</div>
            <div className="absolute top-80 right-20 text-2xl opacity-15 animate-pulse" style={{animationDelay: '2.7s'}}>💡</div>
            <div className="absolute bottom-20 left-1/2 text-xl opacity-20 animate-pulse" style={{animationDelay: '3.2s'}}>🔥</div>
          </div>
          
          {/* Silver Circuit Lines */}
          <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1000 1000">
            <defs>
              <linearGradient id="silverCircuit" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#c0c0c0" />
                <stop offset="50%" stopColor="#e5e5e5" />
                <stop offset="100%" stopColor="#a0a0a0" />
              </linearGradient>
            </defs>
            <path d="M0,200 Q250,100 500,200 T1000,200" stroke="url(#silverCircuit)" strokeWidth="1" fill="none" className="animate-pulse">
              <animate attributeName="stroke-dasharray" values="0,1000;1000,0;0,1000" dur="12s" repeatCount="indefinite" />
            </path>
            <path d="M200,0 Q300,250 400,500 T600,1000" stroke="url(#silverCircuit)" strokeWidth="1" fill="none" className="animate-pulse">
              <animate attributeName="stroke-dasharray" values="0,1000;1000,0;0,1000" dur="10s" repeatCount="indefinite" />
            </path>
            <path d="M800,0 Q700,300 600,600 T400,1000" stroke="url(#silverCircuit)" strokeWidth="1" fill="none" className="animate-pulse">
              <animate attributeName="stroke-dasharray" values="0,1000;1000,0;0,1000" dur="14s" repeatCount="indefinite" />
            </path>
          </svg>
          
          {/* HoppHomes Logo Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-1/4 left-1/4 text-6xl font-bold text-gray-400 rotate-12 animate-pulse">H</div>
            <div className="absolute bottom-1/4 right-1/4 text-6xl font-bold text-gray-400 -rotate-12 animate-pulse" style={{animationDelay: '1s'}}>H</div>
            <div className="absolute top-3/4 left-1/3 text-4xl font-bold text-gray-500 rotate-45 animate-pulse" style={{animationDelay: '2s'}}>AI</div>
          </div>
        </div>
      </div>
      
      {/* Content Overlay */}
      <div className="relative z-10">
      <div className="max-w-6xl mx-auto px-2 sm:px-4">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-white via-silver to-gray-200 bg-clip-text text-transparent mb-2 drop-shadow-lg">HoppAI Saját Ingatlan</h1>
          <p className="text-gray-300 text-sm sm:text-lg drop-shadow-md">🤖 AI-alapú ingatlanfeltöltő rendszer</p>
          
          {/* Development Notice */}
          <div className="mt-4 sm:mt-6 relative">
            <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 backdrop-blur-md border border-red-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-2xl">
              <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                <div className="relative">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50"></div>
                  <div className="absolute inset-0 w-2 h-2 sm:w-3 sm:h-3 bg-red-400 rounded-full animate-ping"></div>
                </div>
                <h2 className="text-sm sm:text-xl font-bold text-white drop-shadow-lg text-center" style={{
                  textShadow: '0 0 20px rgba(239, 68, 68, 0.8), 0 0 40px rgba(239, 68, 68, 0.6), 0 0 60px rgba(239, 68, 68, 0.4)'
                }}>
                  ⚠️ JELENLEG FEJLESZTÉS ALATT! HAMAROSAN INDUL ⚠️
                </h2>
                <div className="relative">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50"></div>
                  <div className="absolute inset-0 w-2 h-2 sm:w-3 sm:h-3 bg-red-400 rounded-full animate-ping"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-center bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-2 sm:p-3 border border-white/[0.08] max-w-4xl mx-auto overflow-x-auto">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center min-w-0 flex-shrink-0">
                  <div className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border-2 transition-all duration-300 ${
                    currentStep >= step.id 
                      ? 'bg-[#e31937] border-[#e31937] text-white shadow-lg shadow-[#e31937]/50' 
                      : 'bg-white/20 border-white/40 text-white backdrop-blur-sm'
                  }`}>
                    <span className="text-xs sm:text-xs md:text-sm">{currentStep > step.id ? '✓' : step.icon}</span>
                  </div>
                  <p className={`text-xs font-medium mt-1 text-center transition-colors duration-300 whitespace-nowrap ${
                    currentStep >= step.id ? 'text-white drop-shadow-md' : 'text-white/70'
                  }`}>
                    <span className="hidden sm:inline">{step.title}</span>
                    <span className="sm:hidden">{step.title.split(' ')[0]}</span>
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-4 sm:w-8 md:w-12 h-0.5 rounded-full transition-all duration-300 flex-shrink-0 ${
                    currentStep > step.id ? 'bg-[#e31937] shadow-lg shadow-[#e31937]/50' : 'bg-white/30'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-white/30 p-4 sm:p-8 mb-6 sm:mb-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-0 mt-6 sm:mt-8">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-200 text-sm sm:text-base ${
              currentStep === 1
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-white/20 text-white border border-white/30 hover:bg-white/30 hover:scale-105 backdrop-blur-md'
            }`}
          >
            ← Előző
          </button>
          
          <button
            onClick={handleNext}
            disabled={currentStep === steps.length}
            className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-200 text-sm sm:text-base ${
              currentStep === steps.length
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#e31937] text-white hover:bg-[#b91c1c] hover:scale-105 shadow-lg'
            }`}
          >
            {currentStep === steps.length ? 'Befejezve' : 'Következő →'}
          </button>
        </div>  
      </div>
      </div>

      {/* Upsell Modals */}
      <BoostUpsellModal
        isOpen={showBoostModal}
        onClose={() => {
          setShowBoostModal(false);
          setShowVideoModal(true);
        }}
        onSelectBoost={handleBoostSelect}
        listingId={listingId}
      />

      <VideoUpsellModal
        isOpen={showVideoModal}
        onClose={() => {
          setShowVideoModal(false);
          setShowPremiumModal(true);
        }}
        onSelectVideo={handleVideoSelect}
        listingId={listingId}
      />

      <PremiumUpsellModal
        isOpen={showPremiumModal}
        onClose={handleSkipUpsells}
        onRequestPremium={handlePremiumSelect}
        listingId={listingId}
      />

      <BundleOfferModal
        isOpen={showBundleModal}
        onClose={() => {
          setShowBundleModal(false);
          setShowBoostModal(true);
        }}
        onSelectBundle={handleBundleSelect}
        listingId={listingId}
      />

      <StagingUpsellModal
        isOpen={showStagingModal}
        onClose={() => {
          setShowStagingModal(false);
          setShowPremiumModal(true);
        }}
        onSelectStaging={handleStagingSelect}
        listingId={listingId}
      />

      <AIProSubscriptionModal
        isOpen={showAIProModal}
        onClose={() => setShowAIProModal(false)}
        onSubscribe={(plan) => {
          console.log('AI PRO subscription:', plan);
          setShowAIProModal(false);
        }}
      />
    </div>
  );
}
